import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { getOperatorUsers } from "../../services/userService"
import { fetchPickingTasksSummary } from "../../services"
import { Button, DialogView, Loader, SearchInput } from "../../components"
import { Pagination, Table } from "antd"
import { pickerColumns } from "./AssignPickingOrders/AssignOrdersColumnConfigs"
import { PICK_TYPE_STYLES } from "../../utils/pickingStyles"
import { pendingRowsFromTasks, buildUndoInReviewPayload } from "./undoInReviewAssignments"

const PICKERS_PER_PAGE = 5
const STEP_PICKERS = "pickers"
const STEP_DISTRIBUTE = "distribute"

const pickerName = (p) => `${p.first_name} ${p.last_name}`

// Undoing hands unfinished work back to the floor, and the picker who left it short is rarely the
// one free to retake it — so the supervisor always names the picker, with nobody preselected.
// A legacy order takes exactly one; a task order may be split across several, and then the
// remaining work is distributed here and sent in the SAME call that lifts the review.
//
// The picker list is scoped to the order's warehouse so the supervisor cannot pick someone the
// backend will refuse (picker_not_in_warehouse) only after the click.
export const UndoInReviewDialog = ({ open, order, warehouseId, submitting, onCancel, onConfirm }) => {
    const { i18n } = useTranslation()
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [selected, setSelected] = useState([])
    const [step, setStep] = useState(STEP_PICKERS)
    const [rowPickers, setRowPickers] = useState({})

    const usesTasks = !!order?.uses_picking_tasks

    const { data: pickerData, isError: pickerQueryFailed, isLoading: loadingPickers } = useQuery(
        ["operator_users_undo_in_review", search, page, warehouseId],
        () => getOperatorUsers({ search, page, per_page: PICKERS_PER_PAGE, warehouse_id: warehouseId }),
        { enabled: !!open, keepPreviousData: true, refetchOnWindowFocus: false }
    )

    // /users/operators throttles with a 202 that carries no `users`, which would otherwise read
    // as "this warehouse has no operators".
    const pickersFailed = pickerQueryFailed || (!!pickerData && !Array.isArray(pickerData.users))
    const totalPickers = pickerData?.meta?.total_count || 0
    const pickerRows = useMemo(
        () => (pickerData?.users || []).map(p => ({ ...p, key: String(p.id) })),
        [pickerData]
    )
    const selectedKeys = useMemo(() => selected.map(p => String(p.id)), [selected])

    // Same cache entry the tasks summary modal fills, so reopening either is free.
    const { data: tasks, isLoading: isLoadingTasks, isFetching: fetchingTasks, isError: tasksFailed } = useQuery(
        ["picking_tasks_summary", order?.id],
        () => fetchPickingTasksSummary(order.id),
        { enabled: !!open && usesTasks && step === STEP_DISTRIBUTE, refetchOnWindowFocus: false }
    )

    const rows = useMemo(() => pendingRowsFromTasks(tasks), [tasks])

    const isSelected = (id) => selected.some(p => p.id === id)

    const toggle = (picker) => {
        const entry = { id: picker.id, name: pickerName(picker) }
        if (!usesTasks) {
            setSelected(isSelected(picker.id) ? [] : [entry])
            return
        }
        setSelected(prev => prev.some(p => p.id === picker.id)
            ? prev.filter(p => p.id !== picker.id)
            : [...prev, entry])
    }

    // The first picker chosen owns the picking order and is the fallback for every task the
    // distribution does not name — including remainder at a location the summary cannot show
    // (stock relocated while the order sat in review), which the re-plan creates on the fly.
    const defaultPicker = selected[0]
    const splitting = usesTasks && selected.length > 1

    const confirm = () => {
        // The distribution survives going back to the picker step, so a picker deselected after
        // the split must not keep their rows — the backend would happily accept them.
        const chosen = new Set(selected.map(p => p.id))
        const assignments = rows.map(row => {
            const rowPicker = rowPickers[row.key]
            return { ...row, picker_id: chosen.has(rowPicker) ? rowPicker : undefined }
        })
        onConfirm(buildUndoInReviewPayload({ pickerId: defaultPicker?.id, assignments }))
    }

    const onPrimary = () => {
        if (splitting && step === STEP_PICKERS) {
            setStep(STEP_DISTRIBUTE)
            return
        }
        confirm()
    }

    const primaryLabel = splitting && step === STEP_PICKERS
        ? i18n.t("list_processing_orders.undo_in_review.continue")
        : i18n.t("list_processing_orders.undo_in_review.confirm")

    return (
        <DialogView open={!!open} setOpen={onCancel} className=" sm:w-full sm:max-w-6xl text-left">
            {/* 8rem is what DialogView adds around this: sm:p-6 plus sm:my-8. Subtracting it keeps
                the dialog inside the window and gives the body whatever height is left. Inline
                because Tailwind 2 has no arbitrary values like max-h-[...]. */}
            <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 8rem)" }}>
            <div className="pr-8">
                <h3 className="text-2xl font-bold text-gray-800">
                    {i18n.t("list_processing_orders.undo_in_review.dialog_title")}
                    {order?.order_number && <span className="text-base text-gray-500 ml-2 font-mono font-normal">#{order.order_number}</span>}
                </h3>
            </div>

            {/* -mx-1 px-1: a scroll container clips BOTH axes, and the search input's focus ring is
                drawn outside its box. The negative margin buys those 4px back without moving
                the content, so the ring is not sliced at the edges. */}
            <div className="mt-3 flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
            {step === STEP_PICKERS && (
                <>
                    <p className="mt-1 text-gray-500 mb-3">
                        {usesTasks
                            ? i18n.t("list_processing_orders.undo_in_review.choose_pickers_hint")
                            : i18n.t("list_processing_orders.undo_in_review.choose_picker_hint")}
                    </p>
                    <SearchInput
                        onChange={(v) => { setSearch(v); setPage(1) }}
                        debounce={250}
                        placeholder={i18n.t("picking_tasks.actions.search_operator_placeholder")}
                        className="w-full mb-3"
                    />
                    {selected.length > 0 && (
                        <p className="text-xs text-gray-600 mb-2">
                            {i18n.t("list_processing_orders.undo_in_review.selected_summary", { names: selected.map(p => p.name).join(", ") })}
                        </p>
                    )}
                    <div className="w-full">
                        <Table
                            rowSelection={{
                                type: usesTasks ? "checkbox" : "radio",
                                preserveSelectedRowKeys: true,
                                selectedRowKeys: selectedKeys,
                                onChange: (_keys, rows) => setSelected(rows.map(r => ({ id: r.id, name: pickerName(r) }))),
                            }}
                            size="small"
                            columns={pickerColumns}
                            dataSource={pickerRows}
                            pagination={false}
                            locale={{ emptyText: i18n.t(pickersFailed
                                ? "list_processing_orders.undo_in_review.pickers_error"
                                : loadingPickers
                                    ? "common.loading"
                                    : "list_processing_orders.undo_in_review.no_pickers") }}
                            onRow={(record) => ({ onClick: () => toggle(record) })}
                        />
                    </div>
                    <div className="flex items-center justify-between mt-3 w-full">
                        <span className="text-sm text-gray-500">
                            {pickersFailed
                                ? ""
                                : i18n.t("list_processing_orders.undo_in_review.operators_count", { count: totalPickers })}
                        </span>
                        <Pagination
                            size="small"
                            total={totalPickers}
                            pageSize={PICKERS_PER_PAGE}
                            current={page}
                            onChange={(p) => setPage(p)}
                            showSizeChanger={false}
                        />
                    </div>
                </>
            )}

            {step === STEP_DISTRIBUTE && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        {i18n.t("list_processing_orders.undo_in_review.distribute_hint", { name: defaultPicker?.name })}
                    </p>
                    {tasksFailed && (
                        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                            {i18n.t("list_processing_orders.undo_in_review.tasks_error")}
                        </div>
                    )}
                    {isLoadingTasks ? <Loader show={true} /> : tasksFailed ? null : (
                        <div className="w-full">
                            {rows.length === 0 && (
                                <div className="text-sm text-gray-500 px-1 py-2">
                                    {i18n.t("list_processing_orders.undo_in_review.no_pending")}
                                </div>
                            )}
                            {rows.map((row) => {
                                const percent = row.expected === 0 ? 0 : Math.round((row.picked / row.expected) * 100)
                                // Accessible name for this row's select: without one a screen reader
                                // announces a bare combo box, and several rows can share a location
                                // (a box task and a loose one, or two boxes), so it carries what
                                // tells them apart on screen.
                                const rowLabel = [
                                    row.location_code || "—",
                                    i18n.t(`picking_tasks.pick_types.${row.pick_type}`),
                                    row.container_barcode,
                                ].filter(Boolean).join(" · ")
                                return (
                                    <div key={row.key} className="border border-gray-200 rounded-lg overflow-hidden mb-3">
                                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                            {row.products.map((product, i) => (
                                                <div key={product.key} className={i > 0 ? "mt-2 pt-2 border-t border-gray-200" : ""}>
                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                        <div className="flex items-baseline gap-2 flex-wrap">
                                                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{i18n.t("picking_tasks.columns.sku")}</span>
                                                            <span className="font-mono text-sm font-semibold text-gray-800">{product.sku}</span>
                                                        </div>
                                                        <span className="text-xs text-gray-500 whitespace-nowrap">
                                                            {i18n.t("list_processing_orders.undo_in_review.pending_count", { count: product.pending })}
                                                        </span>
                                                    </div>
                                                    {product.name && product.name !== product.sku && (
                                                        <div className="mt-1 text-sm text-gray-700">{product.name}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <table className="min-w-full">
                                            <thead className="bg-white">
                                                <tr className="border-b border-gray-100">
                                                    <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.columns.location")}</th>
                                                    <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.columns.pick_type")}</th>
                                                    <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.columns.progress")}</th>
                                                    <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.columns.picker")}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white">
                                                <tr>
                                                    <td className="px-4 py-3 text-sm font-mono">
                                                        {row.location_code || "—"}
                                                        {row.container_barcode && (
                                                            <span className="block text-xs text-gray-500">{row.container_barcode}</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PICK_TYPE_STYLES[row.pick_type] || "bg-gray-100 text-gray-800"}`}>
                                                            {i18n.t(`picking_tasks.pick_types.${row.pick_type}`)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-24 bg-gray-200 rounded-full h-2">
                                                                <div
                                                                    className={`h-2 rounded-full ${percent >= 100 ? "bg-green-500" : "bg-indigo-500"}`}
                                                                    style={{ width: `${Math.min(percent, 100)}%` }}
                                                                />
                                                            </div>
                                                            <span className="text-sm text-gray-600 whitespace-nowrap">{row.picked}/{row.expected} ({percent}%)</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <select
                                                            aria-label={i18n.t("list_processing_orders.undo_in_review.picker_for_location", { location: rowLabel })}
                                                            className="border border-gray-300 rounded text-sm py-1 pl-2 pr-8"
                                                            value={rowPickers[row.key] || ""}
                                                            onChange={(e) => {
                                                                // Read the value BEFORE the updater: React 17 pools the
                                                                // event and nulls its target once the handler returns.
                                                                const picked = e.target.value
                                                                setRowPickers(prev => ({ ...prev, [row.key]: picked ? Number(picked) : undefined }))
                                                            }}
                                                        >
                                                            <option value="">{i18n.t("list_processing_orders.undo_in_review.default_picker_option", { name: defaultPicker?.name })}</option>
                                                            {selected.map(p => (
                                                                <option key={p.id} value={p.id}>{p.name}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </>
            )}

            </div>

            <div className="mt-5 pt-4 border-t border-gray-100 flex gap-3 flex-none">
                {step === STEP_DISTRIBUTE && (
                    <Button className="flex-1" type="secondary" onClick={() => setStep(STEP_PICKERS)} disabled={submitting}>
                        {i18n.t("list_processing_orders.undo_in_review.back")}
                    </Button>
                )}
                <Button className="flex-1" type="secondary" onClick={onCancel} disabled={submitting}>{i18n.t("common.cancel")}</Button>
                <Button className="flex-1" type="primary" onClick={onPrimary} disabled={selected.length === 0 || submitting || (step === STEP_DISTRIBUTE && (fetchingTasks || tasksFailed))}>
                    {primaryLabel}
                </Button>
            </div>
            </div>
        </DialogView>
    )
}
